// ============================================================================
// MIRO SYNC PANEL - frontend/src/components/MiroSyncPanel.tsx
// UI component for Miro board integration: OAuth, template frames, sync, conflicts
// ============================================================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Check, Plus, Loader, Layout, AlertTriangle, ExternalLink, Sparkles, Globe } from 'lucide-react';
import {
  getMiroOAuthStatus,
  getMiroAuthorizeUrl,
  listTemplateFrames,
  createProjectBoard,
  getProjectBoard,
  addTemplateFrameToBoard,
  createSessionFrame,
  syncSessionToMiro,
  getSyncStatus,
  pollBoardChanges,
  listConflicts,
  resolveConflict,
  listBoardFrames,
  ingestFrames,
  MiroTemplateFrame,
  MiroProjectBoard,
  MiroSyncResult,
  MiroSyncStatus,
  MiroConflict,
  MiroOAuthStatus,
  MiroBoardFrame
} from '../services/miro';

interface MiroSyncPanelProps {
  sessionId?: string;
  projectId?: string;
  sessionName?: string;
}

// Step indicator component
function StepIndicator({ currentStep }: { currentStep: number }) {
  const steps = ['Connect', 'Setup', 'Sync'];
  return (
    <div className="flex items-center justify-center gap-0 mb-5">
      {steps.map((label, i) => (
        <React.Fragment key={label}>
          {i > 0 && (
            <div className={`h-0.5 w-10 ${i <= currentStep ? 'bg-primary' : 'bg-slate-200'}`} />
          )}
          <div className="flex flex-col items-center gap-1.5">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
              i < currentStep
                ? 'bg-primary text-white'
                : i === currentStep
                  ? 'bg-primary text-white ring-4 ring-primary/20'
                  : 'bg-slate-100 text-slate-400'
            }`}>
              {i < currentStep ? <Check size={14} /> : i + 1}
            </div>
            <span className={`text-xs font-semibold uppercase tracking-wider ${
              i <= currentStep ? 'text-primary' : 'text-slate-400'
            }`}>{label}</span>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

export function MiroSyncPanel({ sessionId, projectId, sessionName }: MiroSyncPanelProps) {
  // OAuth state
  const [oauthStatus, setOauthStatus] = useState<MiroOAuthStatus | null>(null);

  // Board state
  const [board, setBoard] = useState<MiroProjectBoard | null>(null);
  const [templateFrames, setTemplateFrames] = useState<MiroTemplateFrame[]>([]);
  const [selectedFrameIds, setSelectedFrameIds] = useState<string[]>([]);

  // Sync state
  const [syncStatus, setSyncStatus] = useState<MiroSyncStatus | null>(null);
  const [syncResult, setSyncResult] = useState<MiroSyncResult | null>(null);
  const [conflicts, setConflicts] = useState<MiroConflict[]>([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [polling, setPolling] = useState(false);
  const [creatingBoard, setCreatingBoard] = useState(false);
  const [addingFrameId, setAddingFrameId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Ingest state
  const [boardFrames, setBoardFrames] = useState<MiroBoardFrame[]>([]);
  const [selectedIngestFrameIds, setSelectedIngestFrameIds] = useState<string[]>([]);
  const [ingestEntireBoard, setIngestEntireBoard] = useState(false);
  const [ingesting, setIngesting] = useState(false);
  const [loadingFrames, setLoadingFrames] = useState(false);

  const boardPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Detect Miro auth errors and reset to Connect step
  const isAuthError = (err: any): boolean => {
    const msg = (err.response?.data?.error || err.message || '').toLowerCase();
    const status = err.response?.status;
    return status === 401 || msg.includes('token refresh failed') || msg.includes('oauth tokens not found') || msg.includes('reconnect');
  };

  const handleAuthError = (err: any) => {
    setOauthStatus({ connected: false });
    setError('Miro connection expired. Please reconnect.');
  };

  // Derive current step
  const currentStep = !oauthStatus?.connected ? 0 : !board ? 1 : 2;

  // Load initial state
  const loadState = useCallback(async () => {
    try {
      setLoading(true);

      // Check OAuth
      const oauth = await getMiroOAuthStatus();
      setOauthStatus(oauth);

      if (!oauth.connected || !projectId) {
        setLoading(false);
        return;
      }

      // Load template frames + board in parallel
      const [boardResult, frames] = await Promise.all([
        getProjectBoard(projectId),
        listTemplateFrames()
      ]);
      setBoard(boardResult.board);
      setTemplateFrames(frames);

      if (boardResult.board) {
        // Load sync status and conflicts
        const [status, unresolvedConflicts] = await Promise.all([
          sessionId ? getSyncStatus(projectId, sessionId) : Promise.resolve(null),
          listConflicts(projectId, true)
        ]);
        if (status) setSyncStatus(status);
        setConflicts(unresolvedConflicts);
      } else {
        // Pre-select all frames
        setSelectedFrameIds(frames.map(f => f.frameId));
      }
    } catch (err: any) {
      console.error('Failed to load Miro state:', err);
      if (isAuthError(err)) {
        handleAuthError(err);
      }
    } finally {
      setLoading(false);
    }
  }, [sessionId, projectId]);

  useEffect(() => {
    loadState();
    return () => {
      if (boardPollRef.current) clearInterval(boardPollRef.current);
    };
  }, [loadState]);

  // Load board frames when board is connected (for ingest picker)
  useEffect(() => {
    if (board && projectId) {
      loadBoardFrames();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board?.boardId, projectId]);

  // Poll for OAuth completion
  useEffect(() => {
    if (oauthStatus?.connected) return;

    const interval = setInterval(async () => {
      try {
        const status = await getMiroOAuthStatus();
        if (status.connected) {
          setOauthStatus(status);
          loadState();
        }
      } catch { /* ignore */ }
    }, 3000);

    return () => clearInterval(interval);
  }, [oauthStatus?.connected, loadState]);

  // Handlers
  async function handleCreateBoard() {
    if (!projectId) return;
    try {
      setCreatingBoard(true);
      setError(null);
      setSuccess(null);

      await createProjectBoard(
        projectId,
        sessionName || `Project ${projectId.slice(0, 8)}`,
        selectedFrameIds
      );

      setSuccess('Board creation triggered. This may take a moment...');

      // Poll for board to appear
      boardPollRef.current = setInterval(async () => {
        try {
          const result = await getProjectBoard(projectId);
          if (result.board) {
            setBoard(result.board);
            setCreatingBoard(false);
            setSuccess('Miro board created successfully!');
            if (boardPollRef.current) clearInterval(boardPollRef.current);

            // Create session frame if in session context
            if (sessionId) {
              await createSessionFrame(projectId, sessionId, sessionName || `Session ${sessionId.slice(0, 8)}`);
              const status = await getSyncStatus(projectId, sessionId);
              setSyncStatus(status);
            }
          }
        } catch { /* keep polling */ }
      }, 3000);

      // Stop polling after 60s
      setTimeout(() => {
        if (boardPollRef.current) {
          clearInterval(boardPollRef.current);
          boardPollRef.current = null;
        }
        setCreatingBoard(false);
      }, 60000);
    } catch (err: any) {
      if (isAuthError(err)) { handleAuthError(err); } else { setError(err.response?.data?.error || err.message); }
      setCreatingBoard(false);
    }
  }

  async function handleAddFrame(frameId: string) {
    if (!projectId) return;
    try {
      setAddingFrameId(frameId);
      setError(null);

      await addTemplateFrameToBoard(projectId, frameId);
      setSuccess('Adding frame...');

      // Poll for board update
      const pollInterval = setInterval(async () => {
        try {
          const result = await getProjectBoard(projectId);
          if (result.board?.copiedTemplateFrameIds?.includes(frameId)) {
            setBoard(result.board);
            setAddingFrameId(null);
            setSuccess('Template frame added!');
            clearInterval(pollInterval);
          }
        } catch { /* keep polling */ }
      }, 3000);

      setTimeout(() => {
        clearInterval(pollInterval);
        setAddingFrameId(null);
      }, 60000);
    } catch (err: any) {
      if (isAuthError(err)) { handleAuthError(err); } else { setError(err.response?.data?.error || err.message); }
      setAddingFrameId(null);
    }
  }

  async function handleSync() {
    if (!projectId || !sessionId) return;
    try {
      setSyncing(true);
      setError(null);
      setSuccess(null);
      setSyncResult(null);

      const result = await syncSessionToMiro(projectId, sessionId);
      setSyncResult(result);

      if (result.success) {
        setSuccess(`Synced ${result.itemsCreated} new + ${result.itemsUpdated} updated items to Miro`);
      } else {
        setError(result.errors?.join(', ') || 'Sync failed');
      }

      const status = await getSyncStatus(projectId, sessionId);
      setSyncStatus(status);
    } catch (err: any) {
      if (isAuthError(err)) { handleAuthError(err); } else { setError(err.response?.data?.error || err.message); }
    } finally {
      setSyncing(false);
    }
  }

  async function handlePoll() {
    if (!projectId) return;
    try {
      setPolling(true);
      setError(null);

      const result = await pollBoardChanges(projectId);

      if (result.newConflicts > 0) {
        setConflicts(result.conflicts);
        setSuccess(`Found ${result.newConflicts} changes on the Miro board`);
      } else {
        setSuccess('No changes detected on the Miro board');
      }
    } catch (err: any) {
      if (isAuthError(err)) { handleAuthError(err); } else { setError(err.response?.data?.error || err.message); }
    } finally {
      setPolling(false);
    }
  }

  async function handleResolveConflict(conflictId: string, resolution: 'keep-spectrum' | 'keep-miro') {
    if (!projectId) return;
    try {
      await resolveConflict(projectId, conflictId, resolution);
      setConflicts(prev => prev.filter(c => c.conflictId !== conflictId));
      setSuccess('Conflict resolved');
    } catch (err: any) {
      if (isAuthError(err)) { handleAuthError(err); } else { setError(err.response?.data?.error || err.message); }
    }
  }

  function toggleFrame(frameId: string) {
    setSelectedFrameIds(prev =>
      prev.includes(frameId)
        ? prev.filter(id => id !== frameId)
        : [...prev, frameId]
    );
  }

  function toggleIngestFrame(frameId: string) {
    setSelectedIngestFrameIds(prev =>
      prev.includes(frameId)
        ? prev.filter(id => id !== frameId)
        : [...prev, frameId]
    );
  }

  async function loadBoardFrames() {
    if (!projectId) return;
    try {
      setLoadingFrames(true);
      const frames = await listBoardFrames(projectId);
      setBoardFrames(frames);
    } catch (err: any) {
      console.warn('Failed to load board frames:', err);
    } finally {
      setLoadingFrames(false);
    }
  }

  async function handleIngest() {
    if (!projectId) return;
    try {
      setIngesting(true);
      setError(null);
      setSuccess(null);

      const result = await ingestFrames(
        projectId,
        ingestEntireBoard ? undefined : selectedIngestFrameIds,
        ingestEntireBoard || undefined
      );

      setSuccess(result.message || `Ingestion triggered for ${result.framesProcessed} frame(s). Processing in background...`);
    } catch (err: any) {
      if (isAuthError(err)) { handleAuthError(err); } else { setError(err.response?.data?.error || err.message); }
    } finally {
      setIngesting(false);
    }
  }

  if (loading) {
    return (
      <div className="p-6 flex flex-col items-center justify-center text-slate-500 gap-2">
        <Loader className="animate-spin" size={20} />
        <span className="text-sm">Loading Miro integration...</span>
      </div>
    );
  }

  if (!projectId) {
    return (
      <div className="p-6 flex flex-col items-center justify-center text-center gap-3">
        <div className="w-12 h-12 bg-slate-100 rounded-2xl flex items-center justify-center">
          <Layout size={24} className="text-slate-400" />
        </div>
        <div>
          <p className="text-sm font-medium text-slate-700">No project selected</p>
          <p className="text-xs text-slate-400 mt-1">Miro integration requires a project. Create or select a project first.</p>
        </div>
      </div>
    );
  }

  const copiedFrameIds = board?.copiedTemplateFrameIds || [];
  const addedFrames = templateFrames.filter(f => copiedFrameIds.includes(f.frameId));
  const availableFrames = templateFrames.filter(f => !copiedFrameIds.includes(f.frameId));

  return (
    <div className="space-y-5 p-5 overflow-y-auto max-h-[calc(100vh-12rem)]">
      {/* Header */}
      <div>
        <h3 className="text-lg font-bold text-slate-900">Miro Board</h3>
        <p className="text-sm text-slate-500 mt-0.5">
          Sync workshop content to a collaborative Miro board.
        </p>
      </div>

      {/* Step Indicator */}
      <StepIndicator currentStep={currentStep} />

      {/* Alerts */}
      {error && (
        <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-700">
          {error}
          <button onClick={() => setError(null)} className="ml-2 font-medium underline">Dismiss</button>
        </div>
      )}
      {success && (
        <div className="rounded-xl bg-green-50 border border-green-200 p-3 text-sm text-green-700">
          {success}
          <button onClick={() => setSuccess(null)} className="ml-2 font-medium underline">Dismiss</button>
        </div>
      )}

      {/* Step 1: OAuth */}
      {!oauthStatus?.connected && (
        <div className="rounded-2xl border border-[#e2e8f0] bg-white p-5 flex flex-col items-center text-center gap-4">
          <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center">
            <Layout size={24} className="text-primary" />
          </div>
          <div>
            <h4 className="font-semibold text-slate-900">Connect to Miro</h4>
            <p className="text-sm text-slate-500 mt-1">
              Authorize Spectrum to access your Miro workspace.
            </p>
          </div>
          <a
            href={getMiroAuthorizeUrl()}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-primary text-navy font-semibold rounded-xl px-5 py-2.5 text-sm shadow-md shadow-primary/20 hover:bg-primary/90 transition-colors"
          >
            Connect to Miro
          </a>
        </div>
      )}

      {/* Step 2: Board Setup (no board yet) */}
      {oauthStatus?.connected && !board && (
        <div className="rounded-2xl border border-[#e2e8f0] bg-white p-4 space-y-4">
          <h4 className="text-[11px] font-bold text-primary tracking-widest uppercase">Select Templates</h4>

          {templateFrames.length > 0 ? (
            <div className="grid grid-cols-2 gap-3">
              {templateFrames.map(frame => {
                const isSelected = selectedFrameIds.includes(frame.frameId);
                return (
                  <button
                    key={frame.frameId}
                    type="button"
                    onClick={() => toggleFrame(frame.frameId)}
                    className={`relative rounded-xl border-2 p-3 text-left cursor-pointer transition-all ${
                      isSelected
                        ? 'border-primary bg-primary/5'
                        : 'border-[#e2e8f0] bg-white hover:border-slate-300'
                    }`}
                  >
                    {isSelected && (
                      <div className="absolute top-2 right-2 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                        <Check size={12} className="text-white" />
                      </div>
                    )}
                    <div className="bg-primary/5 rounded-lg h-12 flex items-center justify-center mb-2">
                      <Layout size={18} className="text-primary/40" />
                    </div>
                    <div className="text-sm font-medium text-slate-900">{frame.title}</div>
                    <div className="text-xs text-slate-400 mt-0.5">{frame.itemCount} items</div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center text-center py-6 gap-2">
              <Layout size={32} className="text-slate-300" />
              <p className="text-sm text-slate-500">
                No templates configured. An admin can set up templates in Admin &gt; Miro Templates.
              </p>
            </div>
          )}

          <button
            onClick={handleCreateBoard}
            disabled={creatingBoard}
            className="w-full inline-flex items-center justify-center gap-2 bg-primary text-navy font-semibold rounded-xl py-2.5 text-sm shadow-md shadow-primary/20 hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {creatingBoard ? (
              <><Loader className="animate-spin" size={16} /> Creating Board...</>
            ) : (
              'Create Board'
            )}
          </button>
        </div>
      )}

      {/* Step 3: Board Connected */}
      {oauthStatus?.connected && board && (
        <>
          {/* Board Header */}
          <div className="rounded-2xl border border-[#e2e8f0] bg-white p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald-500" />
                <h4 className="font-semibold text-slate-900 text-sm">Board Connected</h4>
              </div>
              <a
                href={board.boardUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary-dark font-medium"
              >
                Open in Miro <ExternalLink size={12} />
              </a>
            </div>

            {/* Template Frames Section */}
            {(addedFrames.length > 0 || availableFrames.length > 0) && (
              <div className="space-y-2">
                <h5 className="text-[11px] font-bold text-primary tracking-widest uppercase">Template Frames</h5>

                {/* Added frames */}
                {addedFrames.map(frame => (
                  <div key={frame.frameId} className="flex items-center gap-3 rounded-xl border border-emerald-100 bg-emerald-50/50 p-3">
                    <Layout size={16} className="text-emerald-600" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">{frame.title}</div>
                      <div className="text-xs text-slate-400">{frame.itemCount} items</div>
                    </div>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-emerald-600 bg-emerald-100">
                      <Check size={10} /> Added
                    </span>
                  </div>
                ))}

                {/* Available frames to add */}
                {availableFrames.map(frame => (
                  <div key={frame.frameId} className="flex items-center gap-3 rounded-xl border border-[#e2e8f0] p-3">
                    <Layout size={16} className="text-primary/60" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">{frame.title}</div>
                      <div className="text-xs text-slate-400">{frame.itemCount} items</div>
                    </div>
                    <button
                      onClick={() => handleAddFrame(frame.frameId)}
                      disabled={addingFrameId === frame.frameId}
                      className="inline-flex items-center gap-1 rounded-xl border border-primary/30 bg-primary/5 px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary/10 transition-colors disabled:opacity-50"
                    >
                      {addingFrameId === frame.frameId ? (
                        <><Loader className="animate-spin" size={12} /> Adding...</>
                      ) : (
                        <><Plus size={12} /> Add</>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Session Sync Section (only when sessionId provided) */}
          {sessionId && (
            <div className="rounded-2xl border border-[#e2e8f0] bg-white p-4 space-y-4">
              <h4 className="text-[11px] font-bold text-primary tracking-widest uppercase">Session Sync</h4>

              {/* Sync Status */}
              {syncStatus && (
                <div className="grid grid-cols-5 gap-2 text-center">
                  {[
                    { label: 'Personas', count: syncStatus.itemsByType.personas, color: 'bg-blue-50 text-blue-600' },
                    { label: 'Pain Pts', count: syncStatus.itemsByType.painPoints, color: 'bg-red-50 text-red-600' },
                    { label: 'Stories', count: syncStatus.itemsByType.userStories, color: 'bg-amber-50 text-amber-600' },
                    { label: 'Decisions', count: syncStatus.itemsByType.decisions, color: 'bg-emerald-50 text-emerald-600' },
                    { label: 'Actions', count: syncStatus.itemsByType.actions, color: 'bg-violet-50 text-violet-600' },
                  ].map(item => (
                    <div key={item.label} className={`rounded-xl p-2.5 ${item.color}`}>
                      <div className="text-lg font-bold">{item.count}</div>
                      <div className="text-[10px] font-semibold uppercase tracking-wider">{item.label}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Sync Results */}
              {syncResult && (
                <div className="text-xs text-slate-500">
                  Last sync: {new Date(syncResult.syncedAt).toLocaleString()}
                  {syncResult.errors && syncResult.errors.length > 0 && (
                    <div className="mt-1 text-amber-600">
                      {syncResult.errors.length} warning(s): {syncResult.errors[0]}
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2">
                <button
                  onClick={handleSync}
                  disabled={syncing}
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-primary text-navy font-semibold rounded-xl py-2.5 text-sm shadow-md shadow-primary/20 hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  {syncing ? 'Syncing...' : 'Sync to Miro'}
                </button>

                <button
                  onClick={handlePoll}
                  disabled={polling}
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-white border border-[#e2e8f0] text-slate-600 font-semibold rounded-xl py-2.5 text-sm hover:bg-slate-50 transition-colors disabled:opacity-50"
                >
                  {polling ? 'Checking...' : 'Check Changes'}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Ingest from Miro */}
      {oauthStatus?.connected && board && (
        <div className="rounded-2xl border border-[#e2e8f0] bg-white p-4 space-y-4">
          <div className="flex items-center gap-2">
            <Sparkles size={14} className="text-amber-500" />
            <h4 className="text-[11px] font-bold text-primary tracking-widest uppercase">Ingest from Miro</h4>
          </div>
          <p className="text-xs text-slate-500">
            Use AI to analyze frame content, extract entities, and add insights to the knowledge graph.
          </p>

          {/* Entire board toggle */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={ingestEntireBoard}
              onChange={e => setIngestEntireBoard(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
            />
            <div className="flex items-center gap-1.5">
              <Globe size={14} className="text-slate-400" />
              <span className="text-sm text-slate-700">Process entire board</span>
            </div>
          </label>

          {/* Frame picker (when not entire board) */}
          {!ingestEntireBoard && (
            <div className="space-y-2">
              {loadingFrames ? (
                <div className="flex items-center gap-2 text-xs text-slate-400 py-2">
                  <Loader className="animate-spin" size={12} />
                  Loading frames...
                </div>
              ) : boardFrames.length > 0 ? (
                <div className="grid grid-cols-2 gap-2">
                  {boardFrames.map(frame => {
                    const isSelected = selectedIngestFrameIds.includes(frame.frameId);
                    return (
                      <button
                        key={frame.frameId}
                        type="button"
                        onClick={() => toggleIngestFrame(frame.frameId)}
                        className={`relative rounded-xl border-2 p-2.5 text-left cursor-pointer transition-all ${
                          isSelected
                            ? 'border-amber-400 bg-amber-50'
                            : 'border-[#e2e8f0] bg-white hover:border-slate-300'
                        }`}
                      >
                        {isSelected && (
                          <div className="absolute top-1.5 right-1.5 w-4 h-4 bg-amber-500 rounded-full flex items-center justify-center">
                            <Check size={10} className="text-white" />
                          </div>
                        )}
                        <div className="text-xs font-medium text-slate-900 truncate">{frame.title}</div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-slate-400 italic">No frames found on the board.</p>
              )}
            </div>
          )}

          <button
            onClick={handleIngest}
            disabled={ingesting || (!ingestEntireBoard && selectedIngestFrameIds.length === 0)}
            className="w-full inline-flex items-center justify-center gap-2 bg-amber-500 text-white font-semibold rounded-xl py-2.5 text-sm shadow-md shadow-amber-500/20 hover:bg-amber-600 transition-colors disabled:opacity-50"
          >
            {ingesting ? (
              <><Loader className="animate-spin" size={16} /> Ingesting...</>
            ) : (
              <><Sparkles size={16} /> Ingest to Graph</>
            )}
          </button>
        </div>
      )}

      {/* Conflicts */}
      {conflicts.length > 0 && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50/50 p-4 space-y-3">
          <div className="flex items-center gap-2">
            <AlertTriangle size={16} className="text-amber-600" />
            <h4 className="font-semibold text-amber-800 text-sm">
              {conflicts.length} Conflict{conflicts.length !== 1 ? 's' : ''} Detected
            </h4>
          </div>
          <p className="text-xs text-amber-700">
            Items were modified on the Miro board. Choose which version to keep:
          </p>

          <div className="space-y-3">
            {conflicts.map(conflict => (
              <div key={conflict.conflictId} className="rounded-xl border border-amber-200 bg-white p-3 space-y-3">
                <div className="text-sm font-medium text-slate-900">
                  {conflict.entityType} / {conflict.entityId}
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Spectrum</div>
                    <div className="rounded-lg bg-blue-50 p-2 text-blue-800 text-xs whitespace-pre-wrap">
                      {conflict.spectrumValue || '(empty)'}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Miro</div>
                    <div className="rounded-lg bg-amber-50 p-2 text-amber-800 text-xs whitespace-pre-wrap">
                      {conflict.miroValue || '(empty)'}
                    </div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => handleResolveConflict(conflict.conflictId, 'keep-spectrum')}
                    className="rounded-xl border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100 transition-colors"
                  >
                    Keep Ours
                  </button>
                  <button
                    onClick={() => handleResolveConflict(conflict.conflictId, 'keep-miro')}
                    className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700 hover:bg-amber-100 transition-colors"
                  >
                    Keep Miro
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
